import { Component, DestroyRef, OnInit, TemplateRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Toast } from '../../models/toast';
import { ToastService } from '../../services';

/** Pages drawn in the new UI get the compact dark toast; classic keeps its own (Anton, 2026-09-29). */
const MODERN_PREFIXES = ['/v2', '/admin-v2'];

export function isModernToastUrl(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    return MODERN_PREFIXES.some(prefix => path === prefix || path.startsWith(prefix + '/'));
}

@Component({
    selector: 'app-toasts-container',
    templateUrl: './toasts-container.component.html',
    styleUrls: ['./toasts-container.component.scss'],
    // role=status + aria-live: screen readers announce each toast as it appears.
    host: {
        'class': 'ngb-toasts toast-container position-fixed bottom-0 end-0 p-3',
        '[class.jft-toasts--modern]': 'modern',
        // No box while empty: its 32px of padding sat in Safari's bottom bar and over fields.
        '[class.jft-toasts--empty]': '!toast.toasts.length',
        'role': 'status',
        'aria-live': 'polite'
    },
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ToastsContainerComponent implements OnInit {

  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /** True on /v2 and /admin-v2: the compact dark toast. */
  public modern = false;

  constructor(
    public toast: ToastService,
  ) {
  }

  public ngOnInit() {
    this.modern = isModernToastUrl(this.router.url);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.modern = isModernToastUrl(event.urlAfterRedirects));
  }

  public remove(toast: Toast) {
    this.toast.remove(toast);
  }

  public executeAction(toast: Toast, event: Event) {
    event.stopPropagation();
    if (toast.action && toast.action.callback) {
      toast.action.callback();
    }
    this.remove(toast);
  }

  /** The status in words, read before the message (the icon is decorative). */
  public statusWord(status: string): string {
    return ({ success: 'Success:', danger: 'Error:', warning: 'Warning:' } as Record<string, string>)[status] ?? 'Note:';
  }

  public isTemplate(message: string | TemplateRef<any>) {
    return message instanceof TemplateRef;
  }
}
